import { useState } from "react";
import * as Icons from "lucide-react";

const CONF_TONE = {
  High: "text-emerald-700 bg-emerald-50 border-emerald-200",
  Medium: "text-amber-700 bg-amber-50 border-amber-200",
  Low: "text-rose-700 bg-rose-50 border-rose-200",
};

function TraceList({ label, items, icon: Icon, testid }) {
  if (!items || items.length === 0) return null;
  return (
    <div data-testid={testid}>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1 flex items-center gap-1.5">
        <Icon className="h-3.5 w-3.5" />{label}
      </div>
      <ul className="space-y-1">
        {items.map((it, i) => (
          <li key={i} className="flex gap-2 text-[12px] text-slate-600">
            <Icons.Dot className="h-4 w-4 text-slate-300 shrink-0 -ml-1" /><span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// review = the organizer_reviews doc (uses ownership, management_review, scope_counts, decision_trace)
export function OrganizerGovernance({ review }) {
  const [showTrace, setShowTrace] = useState(false);
  const trace = review?.decision_trace;
  if (!trace) return null;

  const ownership = review.ownership || {};
  const mgmt = review.management_review || {};
  const scope = review.scope_counts || {};
  const confTone = CONF_TONE[trace.confidence] || CONF_TONE.Medium;
  const oos = scope.out_of_scope || 0;

  return (
    <div className="rounded-xl border border-indigo-200 bg-white overflow-hidden" data-testid="org-governance">
      <div className="px-4 py-2.5 border-b border-indigo-100 bg-indigo-50/50 flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Icons.ScrollText className="h-4 w-4 text-indigo-600" />
          <span className="text-xs font-semibold text-slate-700">Review Governance</span>
        </div>
        <span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full border ${confTone}`} data-testid="org-gov-confidence">
          {trace.confidence} confidence
        </span>
      </div>

      <div className="p-4 space-y-3">
        {/* Ownership */}
        {ownership.primary_team ? (
          <div className="text-[13px]" data-testid="org-gov-ownership">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1 flex items-center gap-1.5"><Icons.Users className="h-3.5 w-3.5" />Engagement Ownership</div>
            <div className="flex items-center gap-2"><Icons.Building2 className="h-4 w-4 text-blue-600 shrink-0" /><span className="text-slate-500">Primary:</span><span className="font-semibold text-slate-800" data-testid="org-gov-primary-team">{ownership.primary_team}</span></div>
            {(ownership.collaborating_teams || []).length > 0 && (
              <div className="flex items-start gap-2 mt-1"><Icons.UsersRound className="h-4 w-4 text-violet-600 shrink-0 mt-0.5" /><span className="text-slate-500 shrink-0">Collaborating:</span><span className="font-medium text-slate-700">{ownership.collaborating_teams.join(", ")}</span></div>
            )}
            <div className="text-[11px] text-slate-400 mt-1">Routing confidence: <span className="font-medium text-slate-500">{ownership.routing_confidence || "—"}</span>{ownership.routing_type ? ` · ${ownership.routing_type}` : ""}</div>
          </div>
        ) : null}

        {/* Scope classification + practice profile */}
        <div className="flex flex-wrap items-center gap-1.5" data-testid="org-gov-scope">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mr-1">Findings scope:</span>
          <span className="text-[11px] font-medium rounded-full px-2 py-0.5 border border-blue-200 bg-blue-50 text-blue-700">{scope.required || 0} required</span>
          <span className="text-[11px] font-medium rounded-full px-2 py-0.5 border border-slate-200 bg-slate-50 text-slate-600">{scope.recommended || 0} recommended</span>
          <span className={`text-[11px] font-medium rounded-full px-2 py-0.5 border ${oos > 0 ? "border-amber-200 bg-amber-50 text-amber-700" : "border-emerald-200 bg-emerald-50 text-emerald-700"}`}>{oos} out-of-scope</span>
        </div>

        {mgmt.required && (
          <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-2.5 py-2" data-testid="org-gov-mgmt-review">
            <Icons.Flag className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-[12px] text-amber-800"><span className="font-semibold">Management review recommended.</span>{mgmt.reason ? <span className="block text-amber-700/90 mt-0.5">{mgmt.reason}</span> : null}</div>
          </div>
        )}

        {/* AI Decision Trace */}
        <div className="border-t border-slate-100 pt-2.5">
          <button onClick={() => setShowTrace((s) => !s)} data-testid="org-gov-trace-toggle" className="text-xs font-semibold text-indigo-600 hover:underline inline-flex items-center gap-1">
            <Icons.GitBranch className="h-3.5 w-3.5" />AI Decision Trace
            <Icons.ChevronDown className={`h-3.5 w-3.5 transition-transform ${showTrace ? "rotate-180" : ""}`} />
          </button>
          {showTrace && (
            <div className="mt-2.5 rounded-lg border border-indigo-100 bg-indigo-50/40 p-3 space-y-2.5" data-testid="org-gov-trace">
              <div><div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-0.5">Decision</div><div className="text-[12px] text-slate-700 font-medium">{trace.decision}</div></div>
              <TraceList label="Evidence" items={trace.evidence} icon={Icons.FileSearch} testid="org-gov-trace-evidence" />
              <TraceList label="Reasoning" items={trace.reasoning} icon={Icons.Brain} testid="org-gov-trace-reasoning" />
              {(trace.applicable_policies || []).length > 0 && (
                <div data-testid="org-gov-trace-policies"><div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1 flex items-center gap-1.5"><Icons.BookMarked className="h-3.5 w-3.5" />Applicable Policies</div><div className="flex flex-wrap gap-1.5">{trace.applicable_policies.map((p, i) => <span key={i} className="text-[11px] bg-white border border-indigo-200 text-indigo-700 rounded px-1.5 py-0.5">{p}</span>)}</div></div>
              )}
              {(trace.inputs_consumed || []).length > 0 && (
                <div data-testid="org-gov-trace-inputs"><div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1 flex items-center gap-1.5"><Icons.Boxes className="h-3.5 w-3.5" />Inputs Consumed</div><div className="flex flex-wrap gap-1.5">{trace.inputs_consumed.map((p, i) => <span key={i} className="text-[11px] bg-slate-100 text-slate-600 rounded px-1.5 py-0.5">{p}</span>)}</div></div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
